/**
 * Makeshift Taiwan-Mandarin fix for the Userback widget.
 *
 * Userback's built-in `zh-TW` string table is a character-level conversion of
 * its `zh-CN` one: PRC vocabulary throughout (屏幕 / 反饋 / 郵箱 / 文件 / 查找…),
 * a leftover simplified 删除, `save` mistranslated as 保持, `cancel` as 關閉.
 * The table is module-private inside `widget/v1.js` and the SDK exposes no
 * override hook, so the only seam is the DOM: the widget renders plain
 * elements (custom `ub*` / `u*` tags and `userback-*` classes, no shadow root)
 * appended to <body>, with strings as text nodes and `placeholder` / `title`
 * attributes.
 *
 * This module rewrites those strings as they appear. Matching is exact against
 * Userback's *current* strings, so it self-neutralizes: once Userback ships a
 * proper zh-TW table nothing matches and this becomes a no-op (delete it
 * then). Strings customized in the Userback dashboard don't match either, by
 * design.
 */

/** Userback's current zh-TW string → Taiwan usage. Keys are Userback's exact strings.
 *  Full table with rationale (the copy to send Userback): docs/i18n/userback-zh-TW-corrections.md */
export const ZH_TW_OVERRIDES: ReadonlyMap<string, string> = new Map<string, string>([
    ['回饋', '意見回饋'],
    ['選擇', '請選擇'],
    ['在屏幕上繪制', '在螢幕上標示'],
    ['提供對指定本頁面特定區域的反饋意見', '針對本頁面的特定區域提供意見'],
    ['嗨，我們能為您做些什麼？', '嗨，有什麼可以幫您的嗎？'],
    ['功能入口', '點子園地'],
    ['路線圖', '產品藍圖'],
    ['錄製屏幕', '錄製螢幕'],
    ['將您的反饋意見錄製成一段錄像', '以螢幕錄影的方式提供意見'],
    ['基本反饋', '一般意見回饋'],
    ['提供對本頁面一般反饋意見', '對本頁面提供一般性的意見'],
    ['報告錯誤', '回報問題'],
    ['讓我們知道什麼壞了', '告訴我們哪裡出了問題'],
    ['改進建議', '功能建議'],
    ['我們如何可以改進', '告訴我們可以如何改進'],
    ['聯繫我們', '聯絡我們'],
    ['和我們取得聯繫', '與我們取得聯絡'],
    ['查看其它', '查看其他意見'],
    ['查看已經提交的反饋', '查看已送出的意見回饋'],
    ['郵箱地址', '電子郵件'],
    ['選擇優先級', '選擇優先順序'],
    ['請留下評論...', '請留下您的意見…'],
    ['附加截屏', '附加螢幕截圖'],
    ['截屏', '螢幕截圖'],
    ['發送', '送出'],
    ['删除', '刪除'],
    ['謝謝您的反饋!', '感謝您的意見回饋！'],
    ['高亮', '標示'],
    ['塗黑', '遮蔽'],
    ['筆', '畫筆'],
    ['評論', '新增註解'],
    ['保持', '儲存'],
    ['評論...', '在此輸入註解…'],
    ['上傳文件', '上傳檔案'],
    ['文件格式不支持', '不支援此檔案格式'],
    ['查找...', '搜尋…'],
    ['添加標題', '新增標題'],
    ['搜索無結果', '找不到符合的結果'],
    ['所有', '全部'],
    ['錯誤報告', '問題回報'],
    ['屏幕截圖', '螢幕截圖'],
    ['屏幕錄製', '螢幕錄影'],
    ['僅此頁', '僅限此頁'],
    ['你的變更尚未儲存', '您的變更尚未儲存'],
    ['開始錄製', '開始錄影'],
    ['打開麥克風', '開啟麥克風'],
    ['結束錄製', '結束錄影'],
    // Keys Userback's zh-TW table lacks entirely (the widget falls back to English).
    ['Video recording is not supported', '不支援錄影功能'],
    ['A secure HTTPS connection is required for video recording.', '錄影功能需要安全的 HTTPS 連線。'],
]);

/** Strings the widget composes at runtime (count + noun, size limit). */
const COMPOSED_RULES: ReadonlyArray<readonly [RegExp, string]> = [
    [/^(\d+)\s*個反饋意見$/, '$1 則意見回饋'],
    [/^最大:\s*(.+)$/, '上限：$1'],
];

/**
 * Rewrite one string if it is a known Userback zh-TW string. Preserves the
 * surrounding whitespace of the input; returns the input unchanged otherwise.
 */
export function patchZhTwText(text: string): string {
    const trimmed = text.trim();
    if (!trimmed) return text;
    let replacement = ZH_TW_OVERRIDES.get(trimmed);
    if (replacement === undefined) {
        for (const [pattern, template] of COMPOSED_RULES) {
            if (pattern.test(trimmed)) {
                replacement = trimmed.replace(pattern, template);
                break;
            }
        }
    }
    if (replacement === undefined) return text;
    const start = text.indexOf(trimmed);
    return text.slice(0, start) + replacement + text.slice(start + trimmed.length);
}

const PATCHED_ATTRIBUTES = ['placeholder', 'title', 'aria-label'] as const;

/** The widget's made-up tag names that don't start with "ub" (<ubdiv>, <ubspan>… are covered by prefix). */
const USERBACK_TAGS = new Set(['UMODAL', 'UTOOLBAR', 'UTOOLTIP', 'UCLOSE', 'UCLOSEL', 'UTIMER']);

/** True when `node` sits inside something the Userback widget rendered. */
function isInsideUserback(node: Node): boolean {
    for (let el: Element | null = node instanceof Element ? node : node.parentElement; el; el = el.parentElement) {
        const tag = el.tagName;
        if (tag.startsWith('UB') || USERBACK_TAGS.has(tag)) return true;
        if (typeof el.className === 'string' && el.className.includes('userback')) return true;
    }
    return false;
}

function patchTextNode(node: Node): void {
    const value = node.nodeValue;
    if (value === null) return;
    const next = patchZhTwText(value);
    if (next !== value) node.nodeValue = next;
}

function patchAttributes(el: Element): void {
    for (const name of PATCHED_ATTRIBUTES) {
        const value = el.getAttribute(name);
        if (value === null) continue;
        const next = patchZhTwText(value);
        if (next !== value) el.setAttribute(name, next);
    }
}

function patchSubtree(root: Node): void {
    if (root.nodeType === Node.TEXT_NODE) {
        patchTextNode(root);
        return;
    }
    if (!(root instanceof Element)) return;
    patchAttributes(root);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (n.nodeType === Node.TEXT_NODE) patchTextNode(n);
        else patchAttributes(n as Element);
    }
}

/**
 * Start rewriting Userback's zh-TW strings as the widget renders them. Patches
 * what's already in the DOM, then observes `root` for additions, text edits and
 * attribute changes scoped to Userback containers. Returns an uninstall function.
 *
 * No feedback loop: we only write when the value actually changes, and a
 * corrected value never matches an override key again.
 */
export function installUserbackZhTwPatch(root: Node = document.body): () => void {
    for (const el of Array.from((root as ParentNode).querySelectorAll?.('*') ?? [])) {
        if (isInsideUserback(el)) patchSubtree(el);
    }
    const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            if (m.type === 'childList') {
                m.addedNodes.forEach((n) => { if (isInsideUserback(n)) patchSubtree(n); });
            } else if (m.type === 'characterData') {
                if (isInsideUserback(m.target)) patchTextNode(m.target);
            } else if (m.type === 'attributes' && m.target instanceof Element) {
                if (isInsideUserback(m.target)) patchAttributes(m.target);
            }
        }
    });
    observer.observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: [...PATCHED_ATTRIBUTES],
    });
    return () => observer.disconnect();
}
